import { Renderer } from './engine/Renderer'
import { Input } from './engine/Input'
import { Audio } from './engine/Audio'
import { AssetLoader } from './engine/AssetLoader'
import { Game, GameConfig } from './game/Game'
import { HUD } from './ui/HUD'
import { StartScreen } from './ui/StartScreen'
import {
  saveGame,
  loadSaveData,
  restoreGameState,
  startAutoSave,
  stopAutoSave,
  hasQuickSave,
  hasAutoSave
} from './game/SaveLoad'

class AgeOfClaudes {
  private container: HTMLElement
  private renderer: Renderer
  private input: Input
  private audio: Audio
  private game: Game | null = null
  private hud: HUD | null = null
  private startScreen: StartScreen | null = null

  private loadingScreen: HTMLElement

  constructor() {
    this.container = document.getElementById('game-container')!
    this.loadingScreen = document.getElementById('loading-screen')!
    this.renderer = new Renderer()
    this.audio = new Audio()
    this.input = null!  // Initialized after renderer
  }

  async init(): Promise<void> {
    this.updateLoadingStatus('Initializing renderer...')

    // Initialize renderer
    await this.renderer.init(this.container)

    // Initialize input
    this.input = new Input(this.renderer)

    // Initialize audio
    this.updateLoadingStatus('Loading audio engine...')
    await this.audio.init()

    // Load game assets
    this.updateLoadingStatus('Loading game assets...')
    await AssetLoader.load((progress) => {
      this.updateLoadingStatus(`Loading assets... ${Math.floor(progress * 100)}%`)
    })

    this.updateLoadingStatus('Ready!')

    // Show start screen
    this.showStartScreen()

    // Hide loading screen
    setTimeout(() => {
      this.loadingScreen.style.opacity = '0'
      this.loadingScreen.style.transition = 'opacity 0.5s ease'
      setTimeout(() => {
        this.loadingScreen.style.display = 'none'
      }, 500)
    }, 500)
  }

  private updateLoadingStatus(status: string): void {
    const statusEl = document.getElementById('loading-status')
    if (statusEl) {
      statusEl.textContent = status
    }
  }

  private showStartScreen(): void {
    // Check for existing saves
    const hasSave = hasQuickSave() || hasAutoSave()

    this.startScreen = new StartScreen(this.container, {
      onStart: (factionId) => this.startGame(factionId),
      onContinue: hasSave ? () => this.loadGame() : undefined
    })
  }

  private async loadGame(): Promise<void> {
    // Try quicksave first, then autosave
    const saveData = loadSaveData('quicksave') || loadSaveData('autosave')
    if (!saveData) {
      console.error('No save data found')
      return
    }

    // Start audio context (requires user interaction)
    await this.audio.startAudio()

    // Hide start screen
    this.startScreen?.hide()

    // Create game with saved config
    this.game = new Game(this.renderer, this.input, this.audio, saveData.config)

    // Create HUD
    this.hud = new HUD(this.game, this.container)

    // Restore saved state (this replaces the default start() behavior)
    restoreGameState(this.game, saveData)

    // Set up save/load keyboard shortcuts
    this.setupSaveLoadKeys()

    // Start auto-save
    startAutoSave(this.game, 60000)  // Auto-save every 60 seconds

    // Start music
    this.audio.startAmbientMusic()

    // Start game loop
    this.renderer.addTicker((dt) => this.gameLoop(dt))

    this.game.onMessage?.('Game loaded!', 'success')
  }

  private async startGame(factionId: string): Promise<void> {
    // Start audio context (requires user interaction)
    await this.audio.startAudio()

    // Hide start screen
    this.startScreen?.hide()

    // Create game config
    const config: GameConfig = {
      mapWidth: 80,
      mapHeight: 60,
      mapSeed: Math.floor(Math.random() * 1000000),
      tileSize: 48,
      playerFaction: factionId,
      enemyFaction: this.getEnemyFaction(factionId)
    }

    // Create game
    this.game = new Game(this.renderer, this.input, this.audio, config)

    // Create HUD
    this.hud = new HUD(this.game, this.container)

    // Start game
    this.game.start()

    // Set up save/load keyboard shortcuts
    this.setupSaveLoadKeys()

    // Start auto-save
    startAutoSave(this.game, 60000)  // Auto-save every 60 seconds

    // Start game loop
    this.renderer.addTicker((dt) => this.gameLoop(dt))
  }

  private setupSaveLoadKeys(): void {
    document.addEventListener('keydown', (e) => {
      if (!this.game) return

      // F5 = Quick Save
      if (e.key === 'F5') {
        e.preventDefault()
        if (saveGame(this.game, 'quicksave')) {
          this.game.onMessage?.('Game saved!', 'success')
        } else {
          this.game.onMessage?.('Failed to save game.', 'alert')
        }
      }

      // F9 = Quick Load
      if (e.key === 'F9') {
        e.preventDefault()
        const saveData = loadSaveData('quicksave')
        if (saveData) {
          restoreGameState(this.game, saveData)
          this.game.onMessage?.('Game loaded!', 'success')
        } else {
          this.game.onMessage?.('No quicksave found.', 'alert')
        }
      }
    })
  }

  private getEnemyFaction(playerFaction: string): string {
    // Pick a different faction for the enemy
    const factions = ['opus', 'sonnet', 'haiku']
    const available = factions.filter(f => f !== playerFaction)
    return available[Math.floor(Math.random() * available.length)]
  }

  private gameLoop(dt: number): void {
    if (!this.game) return

    // Update game
    this.game.update(dt)

    // Render game
    this.game.render()

    // Update HUD
    this.hud?.update()
  }
}

// Start the game when DOM is ready
document.addEventListener('DOMContentLoaded', async () => {
  const game = new AgeOfClaudes()
  await game.init()
})
